import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const LIMIT = 200;
const slug = (s) => String(s || '').replace(/[^A-Za-z0-9]+/g, '-');

/** Single select with a search box. options: [{ value, label, sub?, tag? }] — every word typed must match label, sub or value.
 *  onSearch(q): big lists — the server sends only the matches as you type (total = how many exist). */
export const SearchSelect = ({ value, options = [], onChange, placeholder = 'Select…', searchPlaceholder = 'Search…', empty = 'Nothing found',
  disabled = false, loading = false, testId, className = '', onSearch, total }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const searched = useRef('');
  useEffect(() => {
    if (!onSearch || q.trim() === searched.current) return undefined;
    const t = setTimeout(() => { searched.current = q.trim(); onSearch(q.trim()); }, 300);
    return () => clearTimeout(t);
  }, [q, onSearch]);
  const hits = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length ? options.filter((o) => words.every((w) => `${o.label} ${o.sub || ''} ${o.value}`.toLowerCase().includes(w))) : options;
  }, [options, q]);
  const cur = options.find((o) => o.value === value);
  const close = (v) => {
    setOpen(v);
    if (v) return;
    setQ('');
    if (onSearch && searched.current) { searched.current = ''; onSearch(''); }
  };
  const more = Math.max((total || 0) - options.length, hits.length - LIMIT, 0);
  return (
    <Popover open={open} onOpenChange={close} modal>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" role="combobox" disabled={disabled} data-testid={testId}
          className={`h-10 w-full justify-between bg-white px-3 font-normal ${className}`}>
          <span className={`truncate ${cur || value ? 'text-gray-900' : 'text-gray-500'}`}>{cur ? cur.label : (value || placeholder)}</span>
          {loading ? <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-60" /> : <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput value={q} onValueChange={setQ} placeholder={searchPlaceholder} data-testid={testId ? `${testId}-search` : undefined} />
          <CommandList className="max-h-72">
            <CommandEmpty>{loading ? 'Searching…' : empty}</CommandEmpty>
            <CommandGroup>
              {hits.slice(0, LIMIT).map((o) => (
                <CommandItem key={o.value} value={o.value} onSelect={() => { onChange(o.value); close(false); }} className="cursor-pointer"
                  data-testid={testId ? `${testId}-opt-${slug(o.value)}` : undefined}>
                  <Check className={`mr-2 h-4 w-4 shrink-0 ${o.value === value ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 text-sm"><span className="truncate">{o.label}</span>
                      {o.tag && <span className="shrink-0 rounded bg-emerald-100 px-1 text-[10px] font-semibold text-emerald-700">{o.tag}</span>}</div>
                    {o.sub && <div className="truncate text-xs text-gray-500">{o.sub}</div>}
                  </div>
                </CommandItem>))}
              {more > 0 && <p className="px-3 py-2 text-xs text-gray-400" data-testid={testId ? `${testId}-more` : undefined}>{more.toLocaleString('en-IN')} more — type to narrow down</p>}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default SearchSelect;
